import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Shield, Lock } from "lucide-react";
import api from "../../lib/api";

/**
 * EstateOS — Roller & İzinler.
 * Bitrix-style permission matrix: entities (rows) × actions (cols) with scope dropdown per cell.
 */

const ENTITY_LABELS = {
  lead: "Müşteri Adayı",
  deal: "Anlaşma",
  contact: "Kişiler",
  company: "Şirket",
  task: "Görev",
  user: "Kullanıcılar",
  role: "Roller",
  invoice: "Fatura",
  forecast: "Tahmin",
};
const ACTION_LABELS = {
  read: "Oku",
  create: "Ekle",
  update: "Düzenle",
  delete: "Sil",
  export: "Dışa Aktar",
  import: "İçe Aktar",
};
const SCOPE_LABELS = {
  all: "Tümü",
  dept_sub: "Departman + alt",
  dept: "Departman",
  self: "Sadece kendi",
  none: "Erişimi reddet",
};
const SCOPE_COLORS = {
  all:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  dept_sub: "bg-sky-50     text-sky-700     border-sky-200",
  dept:     "bg-indigo-50  text-indigo-700  border-indigo-200",
  self:     "bg-amber-50   text-amber-700   border-amber-200",
  none:     "bg-red-50     text-red-700     border-red-200",
};

const EstateRolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [schema, setSchema] = useState({ entities: [], actions: [], scopes: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(null); // draft edited role
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", slug: "", description: "", color: "#22346B" });

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/estateos/roles"),
        api.get("/estateos/roles/schema"),
      ]);
      setRoles(r.data || []);
      setSchema(s.data || { entities: [], actions: [], scopes: [] });
      if (!selectedId && (r.data || []).length) setSelectedId(r.data[0].id);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = useMemo(() => roles.find((r) => r.id === selectedId) || null, [roles, selectedId]);
  const editing = dirty && dirty.id === selectedId ? dirty : selected;

  const setPerm = (entity, action, scope) => {
    setDirty((prev) => {
      const base = prev && prev.id === selectedId ? prev : { ...selected, permissions: { ...(selected?.permissions || {}) } };
      const perms = { ...(base.permissions || {}) };
      perms[entity] = { ...(perms[entity] || {}), [action]: scope };
      return { ...base, permissions: perms };
    });
  };

  const saveRole = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      await api.put(`/estateos/roles/${dirty.id}`, {
        name: dirty.name,
        color: dirty.color,
        description: dirty.description,
        permissions: dirty.permissions,
        order: dirty.order || 0,
      });
      toast.success("Roller güncellendi");
      setDirty(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const createRole = async () => {
    if (!newRole.name.trim() || !newRole.slug.trim()) return toast.error("Ad ve slug zorunlu");
    setBusy(true);
    try {
      const perms = {};
      (schema.entities || []).forEach((e) => {
        perms[e] = {};
        (schema.actions || []).forEach((a) => { perms[e][a] = "none"; });
      });
      const r = await api.post("/estateos/roles", { ...newRole, permissions: perms, order: roles.length + 1 });
      toast.success("Rol oluşturuldu");
      setCreating(false); setNewRole({ name: "", slug: "", description: "", color: "#22346B" });
      await load();
      setSelectedId(r.data?.id);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const deleteRole = async (r) => {
    if (r.is_system) return toast.error("Sistem rolü silinemez");
    if (!window.confirm(`"${r.name}" rolü silinsin mi?`)) return;
    try {
      await api.delete(`/estateos/roles/${r.id}`);
      toast.success("Silindi");
      if (selectedId === r.id) setSelectedId(null);
      setDirty(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const isYonetici = selected?.slug === "yonetici" || selected?.is_system;

  return (
    <div className="h-full flex" data-testid="estateos-roles-page">
      {/* Roles list */}
      <aside className="w-72 flex-shrink-0 bg-white border-r border-black/5 flex flex-col">
        <div className="px-4 py-4 border-b border-black/5">
          <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium">Roller</div>
          <div className="font-serif text-xl text-brand-navy mt-1">Erişim İzinleri</div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {roles.map((r) => (
            <button
              key={r.id}
              onClick={() => { setSelectedId(r.id); setDirty(null); }}
              className={`w-full text-left px-3 py-2.5 rounded-sm flex items-center gap-3 transition-colors ${selectedId === r.id ? "bg-brand-navy text-white" : "hover:bg-brand-light text-brand-navy"}`}
              data-testid={`role-item-${r.id}`}
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color }} />
              <span className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{r.name}</div>
                <div className={`text-[0.62rem] tracking-[0.14em] truncate ${selectedId === r.id ? "text-white/60" : "text-brand-medium"}`}>{r.description || r.slug}</div>
              </span>
              {r.is_system ? <Lock size={11} strokeWidth={1.5} className={selectedId === r.id ? "text-white/70" : "text-brand-medium"} /> : null}
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-black/5">
          <button onClick={() => setCreating(true)} className="w-full inline-flex items-center justify-center gap-2 text-xs tracking-[0.2em] uppercase border border-brand-navy/20 hover:border-brand-navy text-brand-navy py-2 transition-colors" data-testid="new-role-btn">
            <Plus size={12} strokeWidth={1.5} /> Yeni Rol
          </button>
        </div>
      </aside>

      {/* Matrix */}
      <section className="flex-1 min-w-0 overflow-auto p-6 md:p-8">
        {!selected ? (
          <div className="text-brand-medium">Bir rol seçin.</div>
        ) : (
          <>
            <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 text-[0.62rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
                  <Shield size={12} strokeWidth={1.5} /> Rol
                </div>
                {dirty ? (
                  <div className="mt-2 flex items-center gap-3">
                    <input value={dirty.name} onChange={(e) => setDirty({ ...dirty, name: e.target.value })} className="font-serif text-2xl text-brand-navy bg-transparent border-b border-black/10 focus:border-brand-primary outline-none" />
                    <input type="color" value={dirty.color || "#22346B"} onChange={(e) => setDirty({ ...dirty, color: e.target.value })} className="w-8 h-8 border border-black/10 cursor-pointer" />
                  </div>
                ) : (
                  <h2 className="font-serif text-2xl md:text-3xl text-brand-navy mt-2 leading-tight flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full" style={{ background: selected.color }} />
                    {selected.name}
                  </h2>
                )}
                <p className="text-sm text-brand-medium mt-2 max-w-xl">
                  {dirty ? (
                    <textarea value={dirty.description || ""} onChange={(e) => setDirty({ ...dirty, description: e.target.value })} rows={2} className="w-full border border-black/10 px-2 py-1 text-sm outline-none focus:border-brand-primary" />
                  ) : (selected.description || "—")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {dirty ? (
                  <>
                    <button onClick={() => setDirty(null)} className="text-xs uppercase tracking-[0.2em] text-brand-medium px-4 py-2">İptal</button>
                    <button onClick={saveRole} disabled={busy} className="btn-primary px-6 py-2" data-testid="role-save">
                      {busy ? "..." : "Kaydet"}
                    </button>
                  </>
                ) : (
                  <>
                    {!selected.is_system ? (
                      <button onClick={() => deleteRole(selected)} className="text-red-600 hover:text-red-700 text-xs inline-flex items-center gap-1 border border-red-200 hover:border-red-500 px-3 py-2 uppercase tracking-[0.2em]" data-testid="role-delete">
                        <Trash2 size={12} strokeWidth={1.5} /> Rolü Sil
                      </button>
                    ) : (
                      <span className="text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium inline-flex items-center gap-1 px-3 py-2 border border-black/10">
                        <Lock size={12} strokeWidth={1.5} /> Sistem Rolü
                      </span>
                    )}
                    {!isYonetici ? (
                      <button onClick={() => setDirty({ ...selected })} className="btn-primary px-5 py-2" data-testid="role-edit">
                        <Pencil size={12} strokeWidth={1.5} /> Düzenle
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </header>

            {isYonetici && !dirty ? (
              <div className="bg-brand-navy/5 border-s-2 border-brand-navy px-5 py-4 mb-6 text-sm text-brand-navy">
                <strong>Yönetici</strong> rolü tüm izinlere sahiptir ve değiştirilemez. Yeni bir rol oluşturarak farklı yetki seviyeleri tanımlayabilirsiniz.
              </div>
            ) : null}

            {/* Matrix */}
            <div className="bg-white border border-black/5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-light">
                  <tr>
                    <th className="px-4 py-3 text-left text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium sticky left-0 bg-brand-light z-10">Varlık</th>
                    {(schema.actions || []).map((a) => (
                      <th key={a} className="px-4 py-3 text-left text-[0.62rem] tracking-[0.24em] uppercase text-brand-medium">{ACTION_LABELS[a] || a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(schema.entities || []).map((entity) => (
                    <tr key={entity} className="border-t border-black/5">
                      <td className="px-4 py-3 font-medium text-brand-navy sticky left-0 bg-white z-10">{ENTITY_LABELS[entity] || entity}</td>
                      {(schema.actions || []).map((action) => {
                        const scope = editing?.permissions?.[entity]?.[action] || "none";
                        const isEditable = !!dirty && !isYonetici;
                        const color = SCOPE_COLORS[scope] || SCOPE_COLORS.none;
                        return (
                          <td key={action} className="px-3 py-2">
                            {isEditable ? (
                              <select
                                value={scope}
                                onChange={(e) => setPerm(entity, action, e.target.value)}
                                className={`text-xs px-2 py-1.5 border w-full outline-none focus:border-brand-primary ${color}`}
                                data-testid={`perm-${entity}-${action}`}
                              >
                                {(schema.scopes || []).map((sc) => <option key={sc} value={sc}>{SCOPE_LABELS[sc] || sc}</option>)}
                              </select>
                            ) : (
                              <span className={`inline-block text-xs px-2 py-1 border ${color}`}>{SCOPE_LABELS[scope] || scope}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* New role modal */}
      {creating ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-role-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCreating(false)} />
          <form onSubmit={(e) => { e.preventDefault(); createRole(); }} className="relative bg-white w-full max-w-md shadow-2xl">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <div className="font-serif text-lg text-brand-navy">Yeni Rol</div>
              <button type="button" onClick={() => setCreating(false)} className="text-brand-medium hover:text-brand-navy"><X size={18} strokeWidth={1.5} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Row label="Rol Adı"><input required value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value, slug: newRole.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} className="inp" data-testid="new-role-name" /></Row>
              <Row label="Slug (eşsiz kimlik)"><input required value={newRole.slug} onChange={(e) => setNewRole({ ...newRole, slug: e.target.value })} className="inp" placeholder="ornek: satis-muduru" data-testid="new-role-slug" /></Row>
              <Row label="Açıklama"><textarea rows={3} value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} className="inp" /></Row>
              <Row label="Renk"><input type="color" value={newRole.color} onChange={(e) => setNewRole({ ...newRole, color: e.target.value })} className="w-16 h-8 border border-black/10 cursor-pointer" /></Row>
              <p className="text-xs text-brand-medium">Rol oluşturulduktan sonra izin matrisinden her varlık için erişim kapsamlarını ayarlayabilirsiniz.</p>
            </div>
            <div className="px-6 py-4 bg-brand-light flex items-center justify-end gap-2 border-t border-black/10">
              <button type="button" onClick={() => setCreating(false)} className="text-xs uppercase tracking-[0.2em] text-brand-medium px-4 py-2">İptal</button>
              <button type="submit" disabled={busy} className="btn-primary px-6 py-2" data-testid="new-role-save">{busy ? "..." : "Oluştur"}</button>
            </div>
          </form>
        </div>
      ) : null}

      <style>{`.inp { width:100%; border:1px solid rgba(0,0,0,.1); padding:0.5rem 0.65rem; font-size:0.85rem; outline:none; } .inp:focus { border-color:#22346B; }`}</style>
    </div>
  );
};

const Row = ({ label, children }) => (
  <label className="block">
    <div className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-medium font-medium mb-1.5">{label}</div>
    {children}
  </label>
);

export default EstateRolesPage;
